import { Switch } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import type { MRT_Cell } from "mantine-react-table-open";
import { useToast } from "../../../mantine/notifications/useToast";
import type { GeneratorOverview } from "../../../utils/backend-types";
import { buildNestedObject } from "../../BuildingBlocks/Forms/utils/buildNestedObject";
import { BasicConfirmationModal } from "../../BuildingBlocks/Layout/Modals/BasicConfirmationModal/BasicConfirmationModal";
import { type GeneratorFeature } from "../../ComponentListContainer/ComponentList/ComponentEditWizard/Data/utils/getGeneratorFeatures";
import { useGeneratorFeatureMutation } from "./useGeneratorFeatureMutation";

interface GeneratorFeatureSwitchCellProps extends MRT_Cell<GeneratorOverview> {
  feature: GeneratorFeature;
  variantId: number;
}

function GeneratorFeatureSwitchCell({
  row,
  getValue,
  feature,
  variantId
}: GeneratorFeatureSwitchCellProps) {
  const { showErrorToast } = useToast();
  const [isCollapseOpen, { toggle, close, open }] = useDisclosure(false);
  const { editGeneratorFeature } = useGeneratorFeatureMutation(variantId);
  const value = getValue<boolean>();
  function handleSave() {
    editGeneratorFeature.mutate(
      {
        id: row.original.id,
        ...buildNestedObject(feature.key, !value)
      },
      {
        onError: () => {
          const generatorName = row.original.name?.trim();
          showErrorToast(
            `Die Änderungen am Erzeuger${generatorName ? ` ${generatorName}` : ""} konnten nicht gespeichert werden.`
          );
        }
      }
    );
    close();
  }

  function handleSwitchChange() {
    if (value) {
      open();
    } else {
      handleSave();
    }
  }

  return (
    <>
      <Switch
        aria-label={`${feature.label} aktivieren/deaktivieren`}
        checked={value || false}
        onChange={handleSwitchChange}
      />
      <BasicConfirmationModal
        cancelButtonText="Abbrechen"
        confirmationButtonText="Ja, deaktivieren"
        opened={isCollapseOpen}
        title={feature.label}
        toggleModal={toggle}
        onCancel={close}
        onConfirm={handleSave}
      >
        {feature.confirmationText ||
          `Möchten Sie die Funktion "${feature.label}" wirklich deaktivieren?`}
      </BasicConfirmationModal>
    </>
  );
}

export default GeneratorFeatureSwitchCell;
